// Conteúdo de um tipo ou grupo do catálogo: leitura, alteração, validação e pré-visualização por canal. Puro, sem rede nem React, para ser testado.
// A validação ESPELHA a do servidor (core-backoffice, CatalogContentRules), que é quem decide de verdade; aqui serve para avisar enquanto se digita.

export const CHANNELS = [
    { id: 'email', label: 'E-mail' },
    { id: 'push', label: 'Push' },
    { id: 'whatsapp', label: 'WhatsApp' },
    { id: 'app', label: 'No app' }
];
const SMS = { id: 'sms', label: 'SMS' };

/** Canais que o editor oferece: os quatro da spec, mais o SMS só se o conteúdo já o usa (o motor entrega, mas ainda não há provedor). */
export const availableChannels = (content) => (listOf(content?.channels).includes('sms') ? [...CHANNELS, SMS] : CHANNELS);
export const channelLabel = (id) => [...CHANNELS, SMS].find((c) => c.id === id)?.label ?? id;

export const AUDIENCES = [
    { id: 'talent', label: 'Talento' },
    { id: 'consultant', label: 'Consultor' },
    { id: 'other', label: 'Qualquer papel' }
];

export const CONSENT_RULES = [
    { id: 'none', label: 'Nenhuma (essencial: a pessoa não desliga)' },
    { id: 'optOut', label: 'Recebe até desligar (opt-out)' },
    { id: 'optIn', label: 'Só recebe se ligar (opt-in)' }
];

export const WHATSAPP_CATEGORIES = [
    { id: 'utility', label: 'Utilidade' },
    { id: 'authentication', label: 'Autenticação' }
];

export const WHATSAPP_STATUSES = [
    { id: 'pending', label: 'Pendente na Meta' },
    { id: 'approved', label: 'Aprovado' },
    { id: 'rejected', label: 'Rejeitado' }
];

/** Variáveis que o motor sempre preenche, além dos dados que o produtor manda. */
export const SYSTEM_VARIABLES = ['Name', 'FirstName', 'Greeting', 'AppUrl', 'Link'];

export const PUSH_TITLE_MAX = 50;
export const PUSH_BODY_MAX = 140;

// E-mail em blocos: os "suaves" só geram aviso (D-CB4); os tetos largos espelham o servidor (CatalogContentRules.cs) e bloqueiam.
export const EMAIL_SUBJECT_SOFT = 80;
export const EMAIL_PREHEADER_SOFT = 140;
export const EMAIL_BUTTON_SOFT = 30;
export const EMAIL_LINE_MAX = 200;
export const EMAIL_PARAGRAPH_MAX = 2000;
export const EMAIL_PARAGRAPHS_MAX = 30;
export const EMAIL_FOOTER_MAX = 500;
export const EMAIL_BUTTON_TEXT_MAX = 60;

/**
 * Os limites de tamanho em vigor. Os valores vêm da tela de Configuração (GET settings) e são passados às validações e aos contadores; sem eles
 * (ainda carregando, ou falha de rede) valem os padrões abaixo, que são os do servidor sem nada gravado.
 */
export const DEFAULT_LIMITS = Object.freeze({
    subjectSoft: EMAIL_SUBJECT_SOFT, preheaderSoft: EMAIL_PREHEADER_SOFT, buttonSoft: EMAIL_BUTTON_SOFT,
    lineMax: EMAIL_LINE_MAX, paragraphMax: EMAIL_PARAGRAPH_MAX, paragraphsMax: EMAIL_PARAGRAPHS_MAX, footerMax: EMAIL_FOOTER_MAX, buttonTextMax: EMAIL_BUTTON_TEXT_MAX,
    pushTitleMax: PUSH_TITLE_MAX, pushBodyMax: PUSH_BODY_MAX
});

const MESSAGE_LABELS = { push: 'push', whatsapp: 'WhatsApp', sms: 'SMS', app: 'no app' };
const NAME_PATTERN =/^[a-z0-9][a-z0-9._-]*$/;
const FIELD_PATTERN = /^\w+$/;

function listOf(value) { return Array.isArray(value) ? value : []; }
const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const sameName = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();

// ---- dados do tipo (dataSchema) ----------------------------------------------------------------------------------------

/** Os dados do tipo como lista de { name, sensitive, example }. Aceita o formato curto (só nomes) que o servidor também aceita. */
export const dataFields = (content) => listOf(content?.dataSchema).map((entry) => (
    typeof entry === 'string'
        ? { name: entry, sensitive: false, example: '' }
        : { name: entry?.name ?? '', sensitive: !!entry?.sensitive, example: entry?.example ?? '' }));

/** Grava os dados do tipo no formato completo; campo sem marcas fica só com o nome, para o JSON continuar enxuto. */
export const withDataFields = (content, fields) => {
    const next = { ...content };
    if (fields.length === 0) { delete next.dataSchema; return next; }
    next.dataSchema = fields.map((f) => {
        const entry = { name: f.name };
        if (f.sensitive) entry.sensitive = true;
        if (f.example) entry.example = f.example;
        return entry;
    });
    return next;
};

export const knownVariables = (content) => [...SYSTEM_VARIABLES, ...dataFields(content).map((f) => f.name).filter(Boolean)];

// ---- textos e variáveis ------------------------------------------------------------------------------------------------

const PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/g;
const PLACEHOLDER_TEST = /\{\{\s*\w+\s*\}\}/;   // sem a marca g: .test() com g guarda posição entre chamadas

/** Os nomes de variável usados num texto, sem repetir (maiúsculas e minúsculas contam como o mesmo, como no motor). */
export const placeholdersIn = (text) => {
    const found = [];
    for (const match of String(text ?? '').matchAll(PLACEHOLDER)) {
        if (!found.some((n) => sameName(n, match[1]))) found.push(match[1]);
    }
    return found;
};

export const channelText = (content, channel) => {
    const text = content?.texts?.[channel];
    return { title: text?.title ?? '', body: text?.body ?? '' };
};

/** Troca o título ou o corpo de um canal. Título vazio some do JSON; texto sem título nem corpo some do canal. */
export const withChannelText = (content, channel, field, value) => {
    const current = { ...(content?.texts?.[channel] ?? {}) };
    if (value === '' || value == null) delete current[field]; else current[field] = value;
    const texts = { ...(content?.texts ?? {}) };
    if (Object.keys(current).length === 0) delete texts[channel]; else texts[channel] = current;
    const next = { ...content };
    if (Object.keys(texts).length === 0) delete next.texts; else next.texts = texts;
    return next;
};

// ---- canais --------------------------------------------------------------------------------------------------------------

export const enabledChannels = (content) => listOf(content?.channels);

/**
 * Liga ou desliga um canal, mantendo a ordem de preferência (a ordem em que foram ligados). Os textos ficam guardados.
 * Ligar o e-mail num tipo sem e-mail nenhum já abre o editor em blocos (o modelo do serviço é só dos tipos antigos).
 */
export const withChannel = (content, channel, enabled) => {
    const current = enabledChannels(content);
    const channels = enabled ? (current.includes(channel) ? current : [...current, channel]) : current.filter((c) => c !== channel);
    const next = { ...content, channels };
    if (enabled && channel === 'email' && !hasEmailBlocks(next) && !String(next.emailTemplate ?? '').trim()) return withEmailBlocksMode(next);
    return next;
};

export const moveChannel = (content, channel, delta) => {
    const channels = [...enabledChannels(content)];
    const from = channels.indexOf(channel);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= channels.length) return content;
    [channels[from], channels[to]] = [channels[to], channels[from]];
    return { ...content, channels };
};

// ---- e-mail em blocos ---------------------------------------------------------------------------------------------------------

export const hasEmailBlocks = (content) => content?.emailBlocks != null;

/** Os blocos como o editor usa: textos sempre presentes (vazios quando faltam), botão nulo quando não há. */
export const emailBlocks = (content) => {
    const b = isObject(content?.emailBlocks) ? content.emailBlocks : {};
    const text = (v) => (typeof v === 'string' ? v : '');
    return {
        subject: text(b.subject),
        preheader: text(b.preheader),
        title: text(b.title),
        paragraphs: listOf(b.paragraphs).map(text),
        button: isObject(b.button) ? { text: text(b.button.text), link: text(b.button.link) } : null,
        footer: text(b.footer)
    };
};

/** Troca um ou mais blocos. Assunto e parágrafos ficam sempre; os demais somem do JSON quando vazios (botão: só se tiver texto ou link). */
export const withEmailBlocks = (content, patch) => {
    const merged = { ...emailBlocks(content), ...patch };
    const blocks = { subject: merged.subject, paragraphs: merged.paragraphs };
    if (merged.preheader) blocks.preheader = merged.preheader;
    if (merged.title) blocks.title = merged.title;
    if (merged.button && (merged.button.text || merged.button.link)) blocks.button = { text: merged.button.text, link: merged.button.link };
    if (merged.footer) blocks.footer = merged.footer;
    const next = { ...content, emailBlocks: blocks };
    delete next.emailTemplate;
    return next;
};

/** Passa o e-mail para blocos (apaga o nome do modelo). Se já há blocos, não mexe. A saudação entra de partida num parágrafo. */
export const withEmailBlocksMode = (content) => {
    if (hasEmailBlocks(content)) return content;
    return withEmailBlocks(content, { subject: '', paragraphs: ['{{Greeting}}'] });
};

/** Volta ao modelo do serviço (apaga os blocos). */
export const withEmailTemplateMode = (content) => {
    const next = { ...content };
    delete next.emailBlocks;
    return next;
};

const validateEmailBlocks = (c, known, errors, warnings, limits) => {
    const label = 'e-mail';
    const sameKnown = (name) => known.some((k) => sameName(k, name));
    if (!isObject(c.emailBlocks)) { errors.push('Os blocos do e-mail (emailBlocks) devem ser um objeto.'); return; }
    const raw = c.emailBlocks;

    const text = (value, max, what) => {
        if (value == null) return '';
        if (typeof value !== 'string') { errors.push(`${what} deve ser um texto.`); return ''; }
        if (value.length > max) errors.push(`${what} passa de ${max} caracteres.`);
        return value;
    };
    const subject = text(raw.subject, limits.lineMax, 'O assunto do e-mail');
    const preheader = text(raw.preheader, limits.lineMax, 'O pré-cabeçalho do e-mail');
    const title = text(raw.title, limits.lineMax, 'O título do e-mail');
    const footer = text(raw.footer, limits.footerMax, 'O rodapé do e-mail');
    if (!subject.trim()) errors.push('O e-mail precisa de assunto.');

    const paragraphs = [];
    if (raw.paragraphs != null) {
        if (!Array.isArray(raw.paragraphs)) errors.push('Os parágrafos do e-mail devem ser uma lista.');
        else {
            if (raw.paragraphs.length > limits.paragraphsMax) errors.push(`O e-mail passa de ${limits.paragraphsMax} parágrafos.`);
            for (const p of raw.paragraphs) {
                if (typeof p !== 'string') { errors.push('Cada parágrafo do e-mail deve ser um texto.'); continue; }
                if (p.length > limits.paragraphMax) errors.push(`Um parágrafo do e-mail passa de ${limits.paragraphMax} caracteres.`);
                paragraphs.push(p);
            }
        }
    }
    if (!title.trim() && paragraphs.every((p) => !p.trim())) errors.push('O e-mail precisa de título ou de pelo menos um parágrafo.');

    let buttonText = '';
    let buttonLink = '';
    if (raw.button != null) {
        if (!isObject(raw.button)) errors.push('O botão do e-mail deve ser um objeto com texto e link.');
        else {
            buttonText = text(raw.button.text, limits.buttonTextMax, 'O texto do botão do e-mail');
            buttonLink = text(raw.button.link, 2000, 'O link do botão do e-mail');
            if (!buttonText.trim() || !buttonLink.trim()) errors.push('O botão do e-mail precisa de texto e de link.');
            else if (!PLACEHOLDER_TEST.test(buttonLink) && !/^https?:\/\/\S+$/i.test(buttonLink.trim())) {
                errors.push('O link do botão do e-mail deve começar com http:// ou https://, ou ser uma variável como {{Link}}.');
            }
        }
    }

    for (const t of [subject, preheader, title, footer, buttonText, buttonLink, ...paragraphs]) {
        for (const name of placeholdersIn(t)) {
            if (!sameKnown(name)) errors.push(`O texto de ${label} usa {{${name}}}, que não está nos dados do tipo.`);
        }
    }
    // Só avisos: o servidor aceita, mas o e-mail fica pior (assunto cortado na caixa de entrada etc.).
    if (subject.length > limits.subjectSoft) warnings.push(`O assunto do e-mail passa de ${limits.subjectSoft} caracteres e pode ser cortado na caixa de entrada.`);
    if (preheader.length > limits.preheaderSoft) warnings.push(`O pré-cabeçalho passa de ${limits.preheaderSoft} caracteres e pode ser cortado.`);
    if (buttonText.length > limits.buttonSoft) warnings.push(`O texto do botão passa de ${limits.buttonSoft} caracteres; botão curto converte melhor.`);
};

// ---- WhatsApp -------------------------------------------------------------------------------------------------------------

export const whatsappTemplate = (content) => {
    const t = isObject(content?.whatsappTemplate) ? content.whatsappTemplate : {};
    return {
        name: t.name ?? '', language: t.language ?? 'pt_BR', category: t.category ?? 'utility', status: t.status ?? 'pending',
        variables: isObject(t.variables) ? t.variables : {}
    };
};

export const withWhatsappTemplate = (content, patch) => ({ ...content, whatsappTemplate: { ...whatsappTemplate(content), ...patch } });

// ---- campos simples -------------------------------------------------------------------------------------------------------

/** Define um campo; vazio (ou nulo) apaga a chave para o JSON continuar enxuto. */
export const withField = (content, name, value) => {
    const next = { ...content };
    if (value === '' || value == null) delete next[name]; else next[name] = value;
    return next;
};

export const intOrNull = (text) => {
    if (text === '' || text == null) return null;
    const n = Number(text);
    return Number.isInteger(n) ? n : NaN;
};

// ---- validação ---------------------------------------------------------------------------------------------------------------

/**
 * Erros (o servidor recusa) e avisos (o servidor aceita, mas merecem atenção) do conteúdo de um tipo.
 * `groups`: ids dos grupos de limite publicados e ativos, ou nulo para não conferir o grupo (ainda não carregou).
 */
export const validateType = (content, groups = null, limits = DEFAULT_LIMITS) => {
    const errors = [];
    const warnings = [];
    const c = content ?? {};

    if (!AUDIENCES.some((a) => a.id === c.audience)) errors.push('Escolha o público: talento, consultor ou qualquer papel.');
    if (!String(c.category ?? '').trim()) errors.push('A categoria é obrigatória.');

    const rule = c.consentRule ?? 'none';
    const canDisable = c.canDisable === true;
    if (!CONSENT_RULES.some((r) => r.id === rule)) errors.push('A regra de consentimento deve ser none, optOut ou optIn.');
    if (rule === 'optIn' && !canDisable) errors.push('Regra opt-in exige que a pessoa possa desligar o tipo.');
    if (!canDisable && rule !== 'none') errors.push('Tipo essencial (a pessoa não pode desligar) não tem regra de consentimento.');
    if (c.allowsLead === true && c.audience === 'consultant') errors.push('Destinatário sem cadastro só faz sentido para talento ou qualquer público.');

    for (const [key, label] of [['capPerDay', 'O teto diário'], ['capPerWeek', 'O teto semanal']]) {
        if (c[key] != null && (!Number.isInteger(c[key]) || c[key] <= 0)) errors.push(`${label} deve ser um número inteiro maior que zero.`);
    }
    if (!canDisable && (c.capPerDay != null || c.capPerWeek != null || c.capGroup != null)) {
        errors.push('Tipo essencial não tem limite de frequência nem grupo de limite.');
    }
    if (c.capGroup != null && groups && !groups.some((g) => sameName(g, c.capGroup))) {
        errors.push(`O grupo de limite '${c.capGroup}' não existe ou ainda não foi publicado.`);
    }

    // dados do tipo
    const fields = dataFields(c);
    const seen = [];
    for (const f of fields) {
        if (!FIELD_PATTERN.test(f.name)) errors.push('Cada dado do tipo precisa de um nome só com letras, números e sublinhado.');
        else if (seen.some((n) => sameName(n, f.name))) errors.push(`O dado '${f.name}' está repetido.`);
        else seen.push(f.name);
    }
    const known = knownVariables(c);

    // canais
    const channels = enabledChannels(c);
    if (channels.length === 0) errors.push('Ligue pelo menos um canal.');
    if (new Set(channels).size !== channels.length) errors.push('Há um canal repetido.');

    const checkPlaceholders = (label, text) => {
        for (const name of placeholdersIn(text)) {
            if (!known.some((k) => sameName(k, name))) errors.push(`O texto de ${label} usa {{${name}}}, que não está nos dados do tipo.`);
        }
    };

    for (const channel of [...new Set(channels)]) {
        if (![...CHANNELS, SMS].some((a) => a.id === channel)) {
            errors.push(`Canal desconhecido: ${channel}.`);
            continue;
        }
        if (channel === 'email') {
            const hasTemplate = String(c.emailTemplate ?? '').trim() !== '';
            if (hasTemplate && hasEmailBlocks(c)) errors.push('O e-mail usa blocos ou o nome de um modelo do serviço, não os dois.');
            else if (hasEmailBlocks(c)) validateEmailBlocks(c, known, errors, warnings, limits);
            else if (!hasTemplate) errors.push('O canal e-mail precisa de blocos ou do nome do template.');
            else if (!NAME_PATTERN.test(c.emailTemplate)) errors.push('O nome do template de e-mail deve ter minúsculas, números, ponto, hífen ou sublinhado.');
            continue;
        }

        const label = MESSAGE_LABELS[channel] ?? channel;   // as mesmas palavras do servidor
        const { title, body } = channelText(c, channel);
        if (!body.trim()) { errors.push(`O canal ${label} está sem texto.`); continue; }

        if (channel === 'push') {
            if (!title.trim()) errors.push('O push precisa de título.');
            if (title.length > limits.pushTitleMax) errors.push(`O título do push passa de ${limits.pushTitleMax} caracteres.`);
            if (body.length > limits.pushBodyMax) errors.push(`O texto do push passa de ${limits.pushBodyMax} caracteres.`);
            // O push aparece na tela bloqueada: dado marcado como sensível não deve ir nele (regra 3.4 de canais-notificacao-multicanal.md).
            for (const name of [...placeholdersIn(title), ...placeholdersIn(body)]) {
                if (fields.some((f) => f.sensitive && sameName(f.name, name))) {
                    warnings.push(`O push usa {{${name}}}, marcado como sensível, e aparece na tela bloqueada.`);
                }
            }
        }
        if (channel === 'whatsapp') validateWhatsapp(c, known, errors);

        checkPlaceholders(label, title);
        checkPlaceholders(label, body);
    }
    return { errors, warnings };
};

const validateWhatsapp = (content, known, errors) => {
    if (!isObject(content.whatsappTemplate)) { errors.push('O WhatsApp precisa da referência do modelo aprovado pela Meta.'); return; }
    const t = content.whatsappTemplate;
    if (!String(t.name ?? '').trim()) errors.push('O modelo de WhatsApp precisa de nome.');
    if (!String(t.language ?? '').trim()) errors.push('O modelo de WhatsApp precisa de idioma.');
    if (!WHATSAPP_CATEGORIES.some((c) => c.id === t.category)) errors.push('A categoria do modelo de WhatsApp deve ser utility ou authentication.');
    if (t.status !== 'approved') errors.push('O WhatsApp só pode ser um canal do tipo com o modelo aprovado pela Meta.');
    for (const [position, field] of Object.entries(isObject(t.variables) ? t.variables : {})) {
        if (!known.some((k) => sameName(k, field))) errors.push(`A variável ${position} do modelo de WhatsApp aponta para '${field}', que não está nos dados do tipo.`);
    }
};

export const validateGroup = (content) => {
    const errors = [];
    const c = content ?? {};
    for (const [key, label] of [['perDay', 'O limite por dia'], ['perWeek', 'O limite por semana']]) {
        if (c[key] != null && (!Number.isInteger(c[key]) || c[key] <= 0)) errors.push(`${label} deve ser um número inteiro maior que zero.`);
    }
    if (c.perDay == null && c.perWeek == null) errors.push('O grupo precisa de um limite por dia ou por semana.');
    return { errors, warnings: [] };
};

export const validate = (kind, content, groups = null, limits = DEFAULT_LIMITS) => (kind === 'group' ? validateGroup(content) : validateType(content, groups, limits));

// ---- pré-visualização ------------------------------------------------------------------------------------------------------

export const SAMPLE_PERSON = { Name: 'Maria Souza', FirstName: 'Maria', Greeting: 'Olá Maria,', AppUrl: 'https://app.colocar-me.com.br', Link: 'https://app.colocar-me.com.br/exemplo' };

/** Valores de exemplo: os do sistema e, para cada dado do tipo, o exemplo digitado ou o próprio nome entre sinais (‹Nome›). */
export const sampleValues = (content) => {
    const values = { ...SAMPLE_PERSON };
    for (const f of dataFields(content)) if (f.name) values[f.name] = f.example || `‹${f.name}›`;
    return values;
};

/** Preenche as variáveis como o motor faz (sem diferenciar maiúsculas); variável desconhecida vira vazio. */
export const renderText = (text, values) => {
    const lookup = Object.fromEntries(Object.entries(values).map(([k, v]) => [k.toLowerCase(), v]));
    return String(text ?? '').replace(PLACEHOLDER, (_, name) => lookup[name.toLowerCase()] ?? '');
};

export const previewOf = (content, channel) => {
    const values = sampleValues(content);
    const { title, body } = channelText(content, channel);
    return { title: renderText(title, values).replace(/[\r\n]+/g, ' ').trim(), body: renderText(body, values).trim() };
};

// ---- pré-visualização do e-mail em blocos ------------------------------------------------------------------------------------------
//
// ESPELHA o `TemplateRenderer.RenderBlocks` e o `_layout.html` do core-notifications: se um mudar, o outro muda junto. É uma aproximação
// para o editor ver a forma do e-mail; o e-mail verdadeiro, com o HTML que o motor gera, só aparece no envio de teste (C6).

const HTML_ENTITIES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const escapeHtml = (text) => String(text).replace(/[&<>"']/g, (ch) => HTML_ENTITIES[ch]);
const encodeLines = (text) => String(text).replace(/\r\n/g, '\n').split('\n').map(escapeHtml).join('<br>');
const oneLine = (text) => String(text).replace(/[\r\n]+/g, ' ').trim();

/** O link do botão só vale se, preenchido, for http(s): do contrário o motor deixa o botão de fora. */
const safeUrl = (text) => {
    try {
        const url = new URL(text);
        return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
    } catch { return null; }
};

export const SAMPLE_UNSUBSCRIBE_URL = 'https://app.colocar-me.com.br/descadastro/exemplo';

const LAYOUT = (slots) => `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${slots.subject}</title>
</head>
<body style="margin:0;padding:0;background:#f4f4f5;font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#1f2937;">
  ${slots.preheader}
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#f4f4f5;padding:24px 12px;">
    <tr>
      <td align="center">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:560px;background:#ffffff;border-radius:8px;overflow:hidden;">
          <tr>
            <td style="background:#f77513;padding:16px 24px;color:#ffffff;font-size:18px;font-weight:600;">re.colocar-me</td>
          </tr>
          <tr>
            <td style="padding:24px;font-size:15px;line-height:1.55;">
              ${slots.content}
            </td>
          </tr>
          <tr>
            <td style="padding:16px 24px;background:#fafafa;color:#6b7280;font-size:12px;line-height:1.5;">
              Você recebeu este e-mail porque tem uma conta no re.colocar-me. Responda a este e-mail se precisar de ajuda.${slots.unsubscribe}
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
`;

/**
 * O e-mail em blocos preenchido com os dados de exemplo: { subject, preheader, html, text }, ou nulo se o tipo não usa blocos.
 * O link de descadastro entra quando a pessoa pode desligar o tipo (`canDisable`), como no motor.
 */
export const emailPreview = (content) => {
    if (!hasEmailBlocks(content)) return null;
    const values = sampleValues(content);
    const plain = (text) => renderText(text, values).trim();
    const b = emailBlocks(content);

    const subject = oneLine(plain(b.subject));
    const preheader = oneLine(plain(b.preheader));
    const title = plain(b.title);
    const paragraphs = b.paragraphs.map(plain).filter((p) => p.length > 0);
    const footer = plain(b.footer);
    const buttonText = b.button ? oneLine(plain(b.button.text)) : '';
    const buttonLink = b.button ? safeUrl(plain(b.button.link)) : null;
    const hasButton = !!buttonLink && buttonText.length > 0;
    const unsubscribe = content.canDisable === true ? SAMPLE_UNSUBSCRIBE_URL : null;

    let html = '';
    if (title) html += `<h1 style="margin:0 0 16px;font-size:20px;line-height:1.3;color:#1f2937;">${escapeHtml(title)}</h1>\n`;
    for (const p of paragraphs) html += `<p style="margin:0 0 16px;">${encodeLines(p)}</p>\n`;
    if (hasButton) {
        html += '<table role="presentation" cellspacing="0" cellpadding="0" style="margin:8px 0 16px;"><tr>'
            + `<td style="background:#f77513;border-radius:6px;"><a href="${escapeHtml(buttonLink)}" `
            + `style="display:inline-block;padding:12px 24px;color:#ffffff;font-weight:600;text-decoration:none;">${escapeHtml(buttonText)}</a></td></tr></table>\n`;
    }
    if (footer) html += `<p style="margin:16px 0 0;font-size:12px;line-height:1.5;color:#6b7280;">${encodeLines(footer)}</p>\n`;

    let text = '';
    if (title) text += `${title}\n\n`;
    for (const p of paragraphs) text += `${p}\n\n`;
    if (hasButton) text += `${buttonText}: ${buttonLink}\n\n`;
    if (footer) text += `${footer}\n\n`;
    text = `${text.trimEnd()}\n`;
    if (unsubscribe) text += `\nPara parar de receber avisos como este: ${unsubscribe}\n`;

    return {
        subject,
        preheader,
        text,
        html: LAYOUT({
            subject: escapeHtml(subject),
            preheader: preheader ? `<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;">${escapeHtml(preheader)}</div>` : '',
            content: html,
            unsubscribe: unsubscribe
                ? `<br><br><a href="${escapeHtml(unsubscribe)}" style="color:#6b7280;">Parar de receber avisos como este</a>`
                : ''
        })
    };
};

export const newTypeContent = () => ({
    audience: 'talent',
    category: 'lembrete',
    consentRule: 'optOut',
    canDisable: true,
    channels: ['push']
});

export const newGroupContent = () => ({ perDay: 1, perWeek: 3 });
